import { Module } from '../Module';

export interface ILocLinkifierFilter {
  (value: ILocLinkifierModel): string;
}

export class LinkTargetAttributes {
  public static Blank = '_blank';
  public static Self = '_self';
  public static Parent = '_parent';
  public static Top = '_top';
}

// LocalizedStatement:    Literal string that contains 0..n tokens to be linkified.
// LocalizedStatementKey: Localization key for string that contains 0..n tokens to be linkified.
//                        Leave these fields null/undefined to linkify an entire statement rather than a subset. Must provide 1 link in this case.
// Links:                 The links that will be inserted into the statement
export interface ILocLinkifierModel {
  localizedStatement?: string;
  localizedStatementKey?: string;
  links?: ILocLinkifierLinkModel[];
}

// Key:               A token in the statement to be replaced by a link that uses ngTranslate dynamic value format, ie '{REPLACEME}'
//                    Leave undefined when the localized value is the entire statement to be linkified.
// LocalizedValue:    Literal string that is the text of the anchor element.
// LocalizedValueKey: Loc key for string that is the text of the anchor element.
// Url:               Href attribute of the anchor.
// Target:            Target attribute of the anchor.
export interface ILocLinkifierLinkModel {
  key?: string;
  localizedValue?: string;
  localizedValueKey?: string;
  url: string;
  target?: string;
}

export namespace LocLinkify {

  export const DEFAULT_TOKEN: string = '{THE_LINK}';

  // Create an ILocLinkifierModel representing a text containing 0 or more anchor elements. The {localizedStatement}
  // should include tokens corresponding to the {key} in each {link} where anchor elements should be inserted.
  // If localizing with Messages, it may be necessary to pass workaround token interpolation with a hack like
  // Messages.yourMsg({ THE_LINK_TOKEN: '{THE_LINK_TOKEN}' }) as there is not an obvious escape mechanism for { or }.
  // Passing undefined or null as the localizedStatement will use the text of the first link given as its text.
  export function linkifier(localizedStatement?: string, links?: ILocLinkifierLinkModel[]): ILocLinkifierModel {
    const linkifierModel: ILocLinkifierModel = {
      localizedStatement: ((!localizedStatement && links) ? links[0].key : localizedStatement) || '',
      links: links,
    };
    return linkifierModel;
  }

  // Create an ILocLinkifierLinkModel model of anchor element <a href="{url}" target="{target}">{localizedValue}</a> .
  // The {key} should match a token included in an ILocLinkifierModel statement where this link should be placed.
  // If no URL is given, the link URL will be used as the link text.
  // If given, the {target} should be chosen from the members of LinkTargetAttributes.
  export function link(url: string, localizedValue?: string, key?: string, target?: string): ILocLinkifierLinkModel {
    const linkModel: ILocLinkifierLinkModel = {
      url: url,
      localizedValue: localizedValue || url,
      key: key || LocLinkify.DEFAULT_TOKEN,
      target: target,
    };
    return linkModel;
  }

}

// Linkify a loc string. Use ng-bind-html. Samples:
//
// Pug where linkifyMe is an ILocLinkifierModel on the scope.
// span(ng-bind-html="linkifyMe | locLinkifier")
//
// Input without any links.
//{
//  localizedStatement: 'Literal string without any links.',
//}
//
// Linkify an entire statement. Localized statement and link key for the one link is undefined.
//{
//  links: [{
//    localizedValueKey: 'linkLocKey',
//    url: 'http://www.abc.com'
//  }]
//}
//
// Input with two links.
//{
//  localizedStatementKey: 'locKeyForStringWithTwoLinks'
//  links: [
//    {
//      key: '{LINK_A_TOKEN}',
//      localizedValueKey: 'linkALocKey',
//      url: 'http://www.a.com'
//    },{
//      key: '{LINK_B_TOKEN}',
//      localizedValue: 'Link b literal string.',
//      url: 'http://www.b.com'
//    }
//  ]
//}
//
// NOTE: DO NOT USE THIS WITH REACT. For similar functionality in React components,
// use the InterpolatedString component.
//
Module.instance().filter('locLinkifier', [
  '$translate',
  ($translate: ng.translate.ITranslateService): ILocLinkifierFilter => {
    return (model: ILocLinkifierModel) => {
      if (!model) return '';

      const buildInterpolationValuesFn = (links: ILocLinkifierLinkModel[]): any => {
        const result: any = {};
        for(let i = 0; i < links.length; i++) {
          // remove the wrapping braces
          const key = links[i].key;
          const prop = key.substring(1, key.length - 1);

          result[prop] = links[i].key;
        }
        return result;
      };

      const linkifyFn = (value: string, url: string, target: string): string => {
        target = target || LinkTargetAttributes.Blank;
        return '<a href="' + url + '" target="' + target + '">' + value + '</a>';
      };

      // If localized statement is empty/null the entire value is linkified. Assumes there is one link available.
      if (!model.localizedStatement && !model.localizedStatementKey) {
        const theLink = model.links[0];
        const theValue = theLink.localizedValue || $translate.instant(theLink.localizedValueKey);

        return linkifyFn(theValue, theLink.url, theLink.target);
      }

      // Otherwise, replace all link keys with linkified values.
      let result = '';
      if (!model.links) {
        result = model.localizedStatement || $translate.instant(model.localizedStatementKey);
      } else {
        result =  model.localizedStatement || $translate.instant(model.localizedStatementKey, buildInterpolationValuesFn(model.links));

        for (let i = 0; i < model.links.length; i++) {
          const link = model.links[i];
          const value = link.localizedValue || $translate.instant(link.localizedValueKey);

          result = result.replace(link.key, linkifyFn(value, link.url, link.target));
        }
      }

      return result;
    };
  }
]);
